'use client';
import React from 'react';
import { t } from '@utils/translate';
import { renderTranslation } from '@utils/renderTranslation';

import { Heading } from '@ui';
import { Section, Container } from '@layout';
import HomeProjectCard, {
  ctaLabelForProject,
  type HomeProjectEntry,
} from '@pages/home/projects/HomeProjectCard';

interface ProjectsDemoProps {
  sectionID?: string;
  className?: string;
  classes?: string;
  isSpanish?: boolean;
}

const ProjectsDemo: React.FC<ProjectsDemoProps> = ({
  isSpanish = false,
  sectionID,
  className = '',
  classes = '',
}) => {
  const locale = isSpanish ? 'es' : 'en';
  const projectsRaw = t('projects', 'home', locale);
  const projects = (Array.isArray(projectsRaw) ? (projectsRaw as HomeProjectEntry[]) : []).filter(
    (project) => project.hidden !== true
  );
  const featuredIndex = projects.findIndex((p) => p.isFeatured === true);
  const featuredProject = featuredIndex >= 0 ? projects[featuredIndex] : projects[0];
  const gridProjects =
    featuredIndex >= 0
      ? projects.filter((_, i) => i !== featuredIndex)
      : projects.slice(1);
  const tc = (en: string, es: string) => (isSpanish ? es : en);
  const ns = 'home';
  const ctaRaw = renderTranslation(t('projects_cta', ns, locale));
  const ctaLabel =
    ctaRaw != null && ctaRaw !== ''
      ? String(ctaRaw)
      : isSpanish
        ? 'Ver proyecto'
        : 'View project';

  const sectionClass = ['home-projects', classes, className].filter(Boolean).join(' ').trim();

  return (
    <Section sectionID={sectionID} className={sectionClass}>
      <Container containerClasses='page-hero-desc'>
        <Heading as='h3'>{tc('Projects Section', 'Sección de proyectos')}</Heading>
        <p className='sub full intro'>
          {tc(
            'Featured project plus supporting cards: elevation, metadata, and a clear CTA—same pattern as the home page.',
            'Proyecto destacado más tarjetas de apoyo: elevación, metadatos y CTA clara—mismo patrón que la página de inicio.'
          )}
        </p>
      </Container>
      {projects.length > 0 && featuredProject ? (
        <>
          <Container>
            <HomeProjectCard
              project={featuredProject}
              featured
              ctaLabel={ctaLabelForProject(featuredProject, ctaLabel)}
            />
          </Container>
          {gridProjects.length > 0 ? (
            <Container>
              <div className='project-grid'>
                {gridProjects.map((project, idx) => (
                  <HomeProjectCard
                    key={project.id ?? project.project_id ?? idx + 1}
                    project={project}
                    ctaLabel={ctaLabelForProject(project, ctaLabel)}
                  />
                ))}
              </div>
            </Container>
          ) : null}
        </>
      ) : (
        <Container>
          <div style={{ color: 'red', padding: '1rem' }}>
            {tc('No projects found for locale', 'No se encontraron proyectos para el idioma')}: {locale}
          </div>
        </Container>
      )}
    </Section>
  );
};

export default ProjectsDemo;
